import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import { getDetailsData } from '../FlowEfficiencyActions';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

let highchartUtils = require('../../../util/highcharts')(Highcharts);

const chartConfigColumn = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    title: {
      text: 'Flow efficiency (%)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Frequency (Number of work items)',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      color: 'rgba(43,147,185, 0.9)',
      states: {
        hover: {
          color: 'rgba(0,123,170, 0.9)'
        }
      },
      point: {
        events: {}
      }
    },
    column: {
      pointPadding: 0,
      borderWidth: 0.5,
      groupPadding: 0,
      shadow: false
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};

const chartConfigScatterplot = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    title: {
      text: 'Flow efficiency (%)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle Time (days)',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 7
      }
    },
    series: {
      point: {
        events: {}
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data'
    }
  ]
};

const masterchartConfig = {
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  chart: {
    reflow: false,
    borderWidth: 0,
    backgroundColor: null,
    marginLeft: 70,
    marginRight: 10,
    zoomType: 'xy',
    type: 'scatter',
    events: {}
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    showLastTickLabel: true,
    plotBands: [
      {
        id: 'mask-before',
        color: 'rgba(0, 0, 0, 0.1)'
      }
    ],
    title: {
      text: null
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  yAxis: {
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle time (days)',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      formatter: function() {
        return Math.round(this.value / 1440);
      },
      style: {
        fontSize: '12px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 5
      }
    }
  },
  tooltip: {
    formatter: function() {
      return false;
    }
  },
  legend: {
    enabled: false
  },
  series: [
    {
      name: 'Data'
    }
  ]
};

export class FlowEfficiencyChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};

    this.onSelection = this.onSelection.bind(this);
    this.updateDetails = this.updateDetails.bind(this);
  }

  chartConfig = () => {
    const { chartType } = this.props;
    return chartType === 'scatterplot' ? _.cloneDeep(chartConfigScatterplot) : _.cloneDeep(chartConfigColumn);
  };

  componentDidMount = () => {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    const chartConfig = this.chartConfig();
    chartConfig.exporting = this.props.chartExporting;
    masterchartConfig.chart.events.selection = this.onSelection;

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    this.master = new Highcharts['Chart'](`${this.props.id}-master`, masterchartConfig);
    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data.series) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.updateChart(prevProps.data);
      if (!this.props.details.length && this.props.queueLists) {
        this.props.getDetailsData(this.props.dates);
      }
    }

    if (this.props.chartType !== prevProps.chartType) {
      if (this.chart) {
        for (let i = this.chart.series.length - 1; i >= 0; i--) {
          this.chart.series[i].remove(false);
        }
        const chartConfig = this.chartConfig();
        this.chart.update(chartConfig);
      }
    }

    if (this.props.data.series && prevProps.queueLists && !_.isEqual(prevProps.queueLists, this.props.queueLists)) {
      this.props.getDetailsData(this.props.dates);
    }

    if (
      this.props.data.series &&
      (prevProps.details != this.props.details || prevProps.effRange != this.props.effRange)
    ) {
      this.updateDetails();
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.master.reflow();
      this.chart.reflow();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
    this.master.destroy();
  }

  updateChart(oldData = {}) {
    this.master.update(
      {
        xAxis: {
          plotBands: [
            {
              id: 'mask-before',
              color: 'rgba(0, 0, 0, 0.2)'
            }
          ]
        }
      },
      false
    );

    const diff = dataUtils.getDiff(oldData.series, this.props.data.series, 'data');
    highchartUtils.updateSeries(diff, this.master);
    this.master.redraw();

    this.updateDetails();
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.master.update(config);
  }

  updateDetailsScatterplot() {
    const data = this.props.details;

    if (!data.series) {
      return;
    }

    const series = _.map(data.series, (s, k) => {
      return {
        ...s,
        id: k,
        data: _.filter(s.data, b => b.x >= this.props.effRange[0] && b.x <= this.props.effRange[1])
      };
    });

    let min = 100;
    let max = 0;
    _.forEach(series, s => {
      _.forEach(s.data, d => {
        if (min > d.x) min = d.x;
        if (max < d.x) max = d.x;
      });
    });

    this.chart.update(
      {
        xAxis: {
          min,
          max
        },
        series
      },
      true,
      true
    );
  }

  updateDetailsColumns() {
    const data = _.filter(this.props.details, b => {
      return b.x >= this.props.effRange[0] && b.x <= this.props.effRange[1];
    });

    if (!data.length) {
      return;
    }

    let min = 0;
    let max = 100;

    if (data && !!data.length) {
      min = _.first(data).x;
      max = _.last(data).x;
      if (!min) {
        min = 0;
      }
      if (!max) {
        max = 100;
      }
    }

    this.chart.update(
      {
        xAxis: { min, max },
        series: [
          {
            data
          }
        ]
      },
      true,
      true
    );
  }

  updateDetails() {
    if (this.props.chartType === 'scatterplot') {
      this.updateDetailsScatterplot();
    } else {
      this.updateDetailsColumns();
    }
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('fe', this.props.wallmode);
    this.chart.update(newConfig.chart);
    this.master.update(newConfig.master);
  }

  onSelection(e) {
    var extremesObject = e.xAxis[0],
      min = extremesObject.min,
      max = extremesObject.max,
      xAxis = e.target.xAxis[0];

    xAxis.removePlotBand('mask-before');
    xAxis.addPlotBand({
      id: 'mask-before',
      from: xAxis.dataMin,
      to: min,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1
    });

    xAxis.removePlotBand('mask-after');
    xAxis.addPlotBand({
      id: 'mask-after',
      from: max,
      to: xAxis.dataMax,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1
    });

    if (this.master.resetBtn) {
      this.master.resetBtn.destroy();
    }
    this.master.resetBtn = this.master.renderer
      .button(
        'Reset zoom',
        null,
        null,
        () => {
          this.resetExtremes();
        },
        {
          zIndex: 20
        }
      )
      .attr({
        align: 'right',
        title: 'Reset zoom level 1:1'
      })
      .add(this.master.zoomGroupButton)
      .align(
        {
          align: 'right',
          x: 5,
          y: 10
        },
        false,
        null
      );

    const from = moment(min),
      to = moment(max);
    this.props.getDetailsData({
      startDate: from,
      endDate: to
    });

    return false;
  }

  resetExtremes() {
    this.master.xAxis[0].removePlotBand('mask-before');
    this.master.xAxis[0].removePlotBand('mask-after');
    this.props.getDetailsData(this.props.dates);
    this.master.resetBtn.destroy();
    delete this.master.resetBtn;
  }

  onPointClick(comp, ev) {
    const isScatterPlot = !ev.cards;
    comp.props.onPointClick({
      efficiency: ev.x,
      cycleTime: isScatterPlot ? ev.y : undefined,
      cards: isScatterPlot ? [{ details: ev.details, card: ev.card }] : ev.cards
    });
  }

  tooltipFormatter(ev) {
    let details = '';
    const isScatterPlot = !ev.points;
    const points = isScatterPlot ? [ev.point] : ev.points;
    for (let p = 0; p < points.length; p++) {
      if (points[p]) {
        const cards = isScatterPlot ? [points[p].card] : points[p].point.cards;
        for (let i = 0; i < cards.length; i++) {
          if (cards[i]) {
            const point = isScatterPlot ? points[p] : cards[i];
            const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
            details += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(isScatterPlot ? cards[i].name : cards[i].card.name)}</div>`;
          }
        }
      }
    }

    let tooltip;
    if (isScatterPlot) {
      const duration = `${ev.y} day${ev.y != 1 ? 's' : ''}`;
      tooltip = `
        <p class="tooltip-label-size" style="margin: 8px 8px 0;">Flow efficiency: ${ev.x}%</p>
        <p class="tooltip-label-size" style="margin: 4px 8px 0;">Cycle Time: ${duration}</p> 
      `;
    } else {
      tooltip = `
        <p class="tooltip-label-size" style="margin: 8px 8px 0;>Flow efficiency: ${ev.x}%</p> 
        <p class="tooltip-label-size" style="margin: 4px 8px 0;">Frequency: ${ev.y}</p>
      `;
    }

    tooltip += details;

    tooltip += `<p class="tooltip-info-txt">Click on the ${isScatterPlot ? 'dot' : 'bar'} to see the details</p>`;

    return tooltip;
  }

  render() {
    return (
      <div id={this.props.id} className="chart_content chart_tooltip_style">
        <div
          id={`${this.props.id}-details`}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
        <div
          style={{ display: this.props.showNavigator ? 'block' : 'none' }}
          id={`${this.props.id}-master`}
          className="chart_master"
        ></div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getDetailsData: dates => dispatch(getDetailsData(dates))
  };
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    queueLists: state.filters.fe.selectedQueueLists,
    chartType: state.filters.fe.chartType,
    showNavigator: state.filters.fe.showNavigator,
    effRange: state.filters.fe.effRange,
    details: state.fe.details,
    isLoading: state.fe.isLoading,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(FlowEfficiencyChart));
